import {
  type WidgetListPresentationProps,
  type WidgetPresentation,
  WidgetPresentationProvider,
} from "@norn/widget-renderer/catalog"
import type { ReactNode } from "react"
import { HairlineTable } from "@/components/archetypes/HairlineTable"
import { ActionBar } from "@/components/ui/action-bar"
import { Button } from "@/components/ui/button"
import { controlClass } from "@/components/ui/control"
import { PageHeader } from "@/components/ui/page-header"
import { Section } from "@/components/ui/section"

export * from "@norn/widget-renderer/catalog"

function List({ columns, rows, footer }: WidgetListPresentationProps) {
  return (
    <div className="min-w-0 [&_tr:has(input:checked)]:bg-accent">
      <HairlineTable
        columns={columns.map((column, index) => ({
          key: column.key,
          header: column.header,
          cell: (row: WidgetListPresentationProps["rows"][number]) => row.cells[index],
        }))}
        rows={[...rows]}
        getRowKey={(row) => row.key}
        footer={
          footer ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-3">
                {footer}
              </td>
            </tr>
          ) : undefined
        }
      />
    </div>
  )
}

function Detail({ identity, actions, children }: { identity: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <Section>
      <PageHeader identity={identity} actions={actions} />
      <div className="mt-4 divide-y divide-border">{children}</div>
    </Section>
  )
}

const presentation: WidgetPresentation = {
  Button: ({ variant, size, ...props }) => <Button variant={variant ?? "secondary"} size={size ?? "md"} {...props} />,
  Section,
  List,
  Detail,
  Form: Section,
  Actions: ({ children, status }) => (
    <ActionBar className="max-md:bottom-0" status={status}>
      {children}
    </ActionBar>
  ),
  formClassName: "space-y-8",
  fieldClassName: controlClass,
}

/** Only the admin host chooses Console archetypes; shared runtime owns all behavior. */
export function AdminWidgetPresentation({ children }: { children: ReactNode }) {
  return (
    <WidgetPresentationProvider value={presentation}>
      <div className="min-w-0 [&_[role=alert]]:min-w-0 [&_[role=alert]]:[overflow-wrap:anywhere]">{children}</div>
    </WidgetPresentationProvider>
  )
}
